import type { PoiCategory } from "./poi.js";
import { NIGHT_THEME, PAPER_THEME, type Theme } from "./theme.js";

/**
 * Couleurs du plan.
 *
 * Partagées entre l'application et le back-office, et vérifiées automatiquement
 * quant à leur contraste (voir les tests) : sur une carte lue de nuit, sous des
 * projecteurs, une pastille peu contrastée devient invisible.
 */
export const MAP_BACKGROUND = PAPER_THEME.inks.paper;

/**
 * Une pastille ne repose pas sur le papier nu : elle est le plus souvent posée
 * sur l'aplat d'une zone — une scène se trouve dans une zone de scène. Les
 * couleurs sont donc choisies pour tenir le seuil de 3:1 sur **le fond le plus
 * défavorable**, et non sur le seul fond de carte. L'orange et le vert d'origine
 * y tombaient à 2,5:1.
 */
export const POI_COLORS: Record<PoiCategory, string> = {
  stage: "#c9400a",
  food: "#237032",
  bar: "#9c36b5",
  toilets: "#1971c2",
  water: "#0c8599",
  firstaid: "#c92a2a",
  info: "#b35c00",
  charging: "#5f3dc4",
  lockers: "#495057",
  entrance: "#212529",
  exit: "#a61e4d",
  accessible: "#0b7285",
};

/**
 * Couleur des catégories inconnues.
 *
 * Un gris moyen paraissait naturel, mais `#868e96` tombait à 2,99:1 sur le fond
 * de carte — sous le seuil de 3:1 des éléments non textuels, à un centième près.
 * C'est exactement le genre d'écart qu'un coup d'œil ne rattrape pas.
 */
export const POI_FALLBACK_COLOR = "#6c757d";

/**
 * Pastilles sur fond de nuit.
 *
 * Les mêmes catégories, tirées en clair. Les couleurs de jour ne s'y
 * transposent pas : quatre d'entre elles — recharge, consignes, entrée, sortie —
 * tombaient sous le seuil de 3:1 sur le fond sombre, et l'entrée, presque noire,
 * disparaissait purement et simplement.
 */
export const POI_COLORS_NIGHT: Record<PoiCategory, string> = {
  stage: "#ff8c42",
  food: "#69db7c",
  bar: "#da77f2",
  toilets: "#74c0fc",
  water: "#3bc9db",
  firstaid: "#ff8787",
  info: "#ffc078",
  charging: "#b197fc",
  lockers: "#adb5bd",
  entrance: "#e9ecef",
  exit: "#f783ac",
  accessible: "#66d9e8",
};

/** Couleur des catégories inconnues, sur fond de nuit. */
export const POI_FALLBACK_COLOR_NIGHT = "#9a9da1";

/**
 * Aplats des zones.
 *
 * Teintes très pâles : elles situent le camping ou le parking sans prendre le
 * pas sur les pastilles, qui sont ce que l'on cherche. Comme les pastilles,
 * elles n'existaient qu'en version claire — sur fond de nuit, un aplat crème
 * aurait éclairé la moitié de l'écran.
 */
export type ZoneColors = {
  site: string;
  camping: string;
  parking: string;
  stage: string;
  other: string;
};

export const ZONE_COLORS: ZoneColors = {
  site: "#eae2d2",
  camping: "#dceedd",
  parking: "#e3ddf2",
  stage: "#f5e0c4",
  other: "#e4dbc9",
};

export const ZONE_COLORS_NIGHT: ZoneColors = {
  site: "#2a2019",
  camping: "#1d3524",
  parking: "#2b2440",
  stage: "#3a2a16",
  other: "#241d18",
};

/** Tout ce que le style de la carte a besoin de savoir d'un thème. */
export type MapPalette = {
  colors: Record<PoiCategory, string>;
  fallback: string;
  background: string;
  /** Contour des pastilles : la couleur du papier, pour qu'elles s'en détachent. */
  poiStroke: string;
  zones: ZoneColors;
  zoneOutline: string;
  paths: string;
};

export function mapPaletteFor(theme: Theme): MapPalette {
  const night = theme.name === NIGHT_THEME.name;
  return {
    colors: night ? POI_COLORS_NIGHT : POI_COLORS,
    fallback: night ? POI_FALLBACK_COLOR_NIGHT : POI_FALLBACK_COLOR,
    background: theme.inks.paper,
    poiStroke: theme.inks.paper,
    zones: night ? ZONE_COLORS_NIGHT : ZONE_COLORS,
    zoneOutline: theme.inks.edge,
    paths: theme.inks.rule,
  };
}
